import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser'
import { apiClient } from '@src/services/http-common'
import { JWT_LOCAL_STORAGE_KEY, loadUserFromJwtString } from '@src/contexts/user-context'
import { protectedResources } from '@src/services/Authentification'
import type { AxiosError, InternalAxiosRequestConfig } from 'axios'

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

let refreshPromise: Promise<string> | null = null

async function refreshToken(msalInstance: IPublicClientApplication): Promise<string> {
  const account = msalInstance.getActiveAccount()
  if (!account) throw new Error('No active MSAL account')

  const response = await msalInstance.acquireTokenSilent({
    account,
    scopes: protectedResources.getTokenAzure.scopes.read,
  })

  const { data } = await apiClient.post<{ jwt: string }>('/GenerateJwtToken', {
    jwt: response.accessToken,
  })

  apiClient.defaults.headers.common.Authorization = `Bearer ${data.jwt}`
  localStorage.setItem(JWT_LOCAL_STORAGE_KEY, data.jwt)
  loadUserFromJwtString(data.jwt)

  return data.jwt
}

export function setupAuthInterceptor(msalInstance: IPublicClientApplication): number {
  return apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined

      if (
        error.response?.status !== 401 ||
        !config ||
        config._retry ||
        config.url?.includes('/GenerateJwtToken')
      ) {
        throw error
      }

      config._retry = true

      try {
        if (!refreshPromise) {
          refreshPromise = refreshToken(msalInstance).finally(() => {
            refreshPromise = null
          })
        }

        const jwt = await refreshPromise
        config.headers.Authorization = `Bearer ${jwt}`
        return apiClient(config)
      } catch (refreshError) {
        if (refreshError instanceof InteractionRequiredAuthError) {
          await msalInstance.loginRedirect({
            scopes: protectedResources.getTokenAzure.scopes.read,
          })
        }
        throw refreshError
      }
    },
  )
}

export function removeAuthInterceptor(interceptorId: number): void {
  apiClient.interceptors.response.eject(interceptorId)
}
